import React, { useRef, useState } from "react";
import { Loader2, Paperclip, ScanSearch, X } from "lucide-react";
import { verifyCheck, verifyJob } from "../lib/api";
import { VerdictCard } from "./verify/VerdictCard";

const poll = async (id, tries = 30) => {
  for (let i = 0; i < tries; i += 1) {
    await new Promise((r) => setTimeout(r, 3000));
    const j = await verifyJob(id);
    if (j.status === "done") return j.verdict;
  }
  throw new Error("Still checking — open the result link in a minute");
};

export const VerifyBar = ({ compact = false }) => {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const pick = useRef(null);
  const key = (() => { try { return localStorage.getItem("verify_key") || ""; } catch (e) { return ""; } })();

  const run = async () => {
    if (!text.trim() && !file) return;
    setErr(""); setRes(null); setBusy("Checking…");
    try {
      const fd = new FormData();
      if (text.trim()) fd.append("text", text.trim());
      if (file) fd.append("file", file);
      if (key) fd.append("key", key);
      const r = await verifyCheck(fd);
      if (r.status === "refused") { setErr(r.message); return; }
      if (r.status === "checking") { setBusy("Checking, one minute…"); setRes({ id: r.job_id, v: await poll(r.job_id) }); return; }
      setRes({ id: r.job_id, v: r.verdict });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Check failed");
    } finally { setBusy(""); }
  };
  const again = () => { setRes(null); setText(""); setFile(null); };

  if (res) return <VerdictCard jobId={res.id} v={res.v} onAgain={again} />;
  return (
    <div className={`rounded-2xl border border-cyan-500/30 bg-[#050b14] ${compact ? "p-4" : "p-5"} space-y-3`} data-testid="verify-bar">
      <div className="flex items-center gap-2">
        <ScanSearch className="w-5 h-5 text-[#00F0FF]" />
        <p className="font-bold text-cyan-50">Verify Bar</p>
        <span className="text-[11px] text-cyan-200/50">paste a message, link or email — or attach a photo, letter, voice note or video</span>
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={compact ? 3 : 6} placeholder="Paste a message, link, Facebook / Instagram / TikTok / YouTube clip link, or full email (with headers) here…"
        className="w-full rounded-xl bg-black/40 border border-cyan-500/30 p-3 text-cyan-50 placeholder:text-cyan-200/30 outline-none focus:border-[#00F0FF]" data-testid="verify-text-input" />
      {file && <p className="text-xs text-cyan-100 flex items-center gap-2" data-testid="verify-file-name">{file.name} · {(file.size / 1e6).toFixed(1)} MB <button onClick={() => setFile(null)} data-testid="verify-file-clear"><X className="w-3.5 h-3.5" /></button></p>}
      {err && <p className="text-sm text-rose-300" data-testid="verify-error">{err}</p>}
      <div className="flex gap-2">
        <input ref={pick} type="file" hidden accept="image/*,audio/*,video/*,.pdf,.eml,message/rfc822" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="verify-file-input" />
        <button onClick={() => pick.current?.click()} className="rounded-full px-4 py-2 border border-cyan-400/50 text-cyan-200 flex items-center gap-2" data-testid="verify-attach-btn"><Paperclip className="w-4 h-4" /> Attach</button>
        <button onClick={run} disabled={!!busy || (!text.trim() && !file)} className="ml-auto rounded-full px-6 py-2 bg-[#00F0FF] text-[#030712] font-black flex items-center gap-2 disabled:opacity-50" data-testid="verify-check-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" />} {busy || "Check it"}
        </button>
      </div>
      <p className="text-[11px] text-cyan-200/40">Results are likely, never proof. Uploads are deleted within 24 hours.</p>
    </div>
  );
};
